import { Button } from '@base-ui/react';
import type { CSSObject, Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { DISABLED_EXCLUSIONS, DISABLED_SELECTORS, focusRing } from '../../interaction-states';
import { forHover } from '../../responsive';
import { typographyVariantStyles } from '../../typography-variants';
import type {
  ButtonColor,
  ButtonRoundness,
  ButtonSize,
  ButtonVariant,
  IconButtonSize,
} from './types';

type ColorFamily = {
  solid: string;
  onColor: string;
  hover: string;
  active: string;
  subtle: string;
  text: string;
  subtleHover: string;
  subtleActive: string;
};

function getColorFamily(theme: Theme): Record<ButtonColor, ColorFamily> {
  return {
    primary: {
      solid: theme.colors.brand.solid,
      onColor: theme.colors.brand.onBrand,
      hover: theme.colors.brand.hover,
      active: theme.colors.brand.active,
      subtle: theme.colors.brand.subtle,
      text: theme.colors.brand.text,
      subtleHover: theme.colors.brand.subtleHover,
      subtleActive: theme.colors.brand.subtleActive,
    },
    secondary: {
      solid: theme.colors.info.solid,
      onColor: theme.colors.info.onInfo,
      hover: theme.colors.info.hover,
      active: theme.colors.info.active,
      subtle: theme.colors.info.subtle,
      text: theme.colors.info.text,
      subtleHover: theme.colors.info.subtleHover,
      subtleActive: theme.colors.info.subtleActive,
    },
    destructive: {
      solid: theme.colors.error.solid,
      onColor: theme.colors.error.onError,
      hover: theme.colors.error.hover,
      active: theme.colors.error.active,
      subtle: theme.colors.error.subtle,
      text: theme.colors.error.text,
      subtleHover: theme.colors.error.subtleHover,
      subtleActive: theme.colors.error.subtleActive,
    },
    warning: {
      solid: theme.colors.warning.solid,
      onColor: theme.colors.warning.onWarning,
      hover: theme.colors.warning.hover,
      active: theme.colors.warning.active,
      subtle: theme.colors.warning.subtle,
      text: theme.colors.warning.text,
      subtleHover: theme.colors.warning.subtleHover,
      subtleActive: theme.colors.warning.subtleActive,
    },
    tertiary: {
      solid: theme.colors.neutral.solid,
      onColor: theme.colors.neutral.onNeutral,
      hover: theme.colors.neutral.hover,
      active: theme.colors.neutral.active,
      subtle: theme.colors.neutral.subtle,
      text: theme.colors.neutral.onNeutral,
      subtleHover: theme.colors.neutral.subtleHover,
      subtleActive: theme.colors.neutral.subtleActive,
    },
  };
}

type ButtonStateStyles = {
  default: CSSObject;
  hover?: CSSObject;
  active?: CSSObject;
};

function getVariantStyles(
  theme: Theme,
  color: ButtonColor,
): Record<ButtonVariant, ButtonStateStyles> {
  const { solid, onColor, hover, active, subtle, text, subtleHover, subtleActive } =
    getColorFamily(theme)[color];

  return {
    solid: {
      default: {
        backgroundColor: solid,
        color: onColor,
      },
      hover: {
        backgroundColor: hover,
      },
      active: {
        backgroundColor: active,
      },
    },

    subtle: {
      default: {
        backgroundColor: subtle,
        color: text,
      },
      hover: {
        backgroundColor: subtleHover,
      },
      active: {
        backgroundColor: subtleActive,
      },
    },

    outline: {
      default: {
        backgroundColor: theme.colors.black[4],
        color: text,
        border: `1px solid ${theme.colors.border.default}`,
      },
      hover: {
        border: `1px solid ${theme.colors.border.strong}`,
      },
      active: {
        backgroundColor: theme.colors.neutral.subtleActive,
        border: `1px solid ${theme.colors.border.strong}`,
      },
    },

    ghost: {
      default: {
        backgroundColor: theme.colors.black[4],
        color: text,
      },
      hover: {
        // Figma draws neutral/subtle-hover here, which is the page canvas colour, so the hover
        // would be invisible. Keep the shared hover surface until design picks a neutral token.
        backgroundColor: color === 'tertiary' ? theme.colors.state.hover : subtleHover,
      },
      active: {
        backgroundColor: subtleActive,
      },
    },
  };
}

function withSolidFill(base: ButtonStateStyles, solid: ButtonStateStyles): ButtonStateStyles {
  return {
    default: { ...base.default, ...solid.default },
    hover: { ...base.hover, ...solid.hover },
    active: { ...base.active, ...solid.active },
  };
}

function getBaseStyles(
  theme: Theme,
  color: ButtonColor,
  variant: ButtonVariant,
  roundness: ButtonRoundness,
  defaultRadius: number,
  selected = false,
  loading = false,
) {
  const variants = getVariantStyles(theme, color);
  const {
    default: baseStyles,
    hover,
    active,
  } = selected ? withSolidFill(variants[variant], variants.solid) : variants[variant];

  return {
    // Layout
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box' as const,
    whiteSpace: 'nowrap' as const,

    // Reset
    border: 'none',
    outline: 'none',
    textDecoration: 'none',
    textTransform: 'initial',
    minWidth: 'initial',

    // Shape
    borderRadius: roundness === 'round' ? 9999 : defaultRadius,

    // Interaction
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    transition: theme.motion.hover,

    // Variant-specific: colors, borders, hover/focus states
    ...baseStyles,

    [forHover]: {
      [`&:hover${DISABLED_EXCLUSIONS}`]: hover,
    },

    [`&:active${DISABLED_EXCLUSIONS}`]: active,

    ...focusRing(theme),

    [DISABLED_SELECTORS]: loading
      ? { cursor: 'progress' }
      : {
          opacity: 0.5,
          cursor: 'not-allowed',
        },
  };
}

const BUTTON_SIZE_CONFIG: Record<
  ButtonSize,
  {
    padding: string;
    gap: number;
    iconSize: number;
    radius: number;
    typography: (typeof typographyVariantStyles)[keyof typeof typographyVariantStyles];
  }
> = {
  sm: {
    padding: '6px 12px',
    gap: 4,
    iconSize: 16,
    radius: 8,
    typography: typographyVariantStyles.body3,
  },
  md: {
    padding: '10px 12px',
    gap: 8,
    iconSize: 18,
    radius: 8,
    typography: typographyVariantStyles.body3,
  },
  lg: {
    padding: '12px 12px',
    gap: 8,
    iconSize: 20,
    radius: 8,
    typography: typographyVariantStyles.body2,
  },
  xl: {
    padding: '14px 16px',
    gap: 8,
    iconSize: 20,
    radius: 12,
    typography: typographyVariantStyles.body2,
  },
};

const TRANSIENT_PROPS = new Set([
  '$color',
  '$variant',
  '$size',
  '$roundness',
  '$fullWidth',
  '$loading',
]);

export const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => !TRANSIENT_PROPS.has(prop),
})<{
  $color: ButtonColor;
  $variant: ButtonVariant;
  $size: ButtonSize;
  $roundness: ButtonRoundness;
  $fullWidth?: boolean;
  $loading: boolean;
}>(({ theme, $color, $variant, $size, $roundness, $fullWidth, $loading }) => {
  const { padding, gap, iconSize, radius, typography } = BUTTON_SIZE_CONFIG[$size];

  return {
    ...getBaseStyles(theme, $color, $variant, $roundness, radius, false, $loading),
    ...typography,
    fontWeight: 500,
    padding,
    gap,
    width: $fullWidth ? '100%' : 'fit-content',

    svg: {
      width: iconSize,
      height: iconSize,
      flexShrink: 0,
    },
  };
});

const ICON_BUTTON_SIZE_CONFIG: Record<
  IconButtonSize,
  { padding: number; iconSize: number; radius: number }
> = {
  xs: { padding: 8, iconSize: 16, radius: 8 },
  sm: { padding: 12, iconSize: 16, radius: 8 },
  md: { padding: 16, iconSize: 16, radius: 8 },
  lg: { padding: 16, iconSize: 20, radius: 12 },
};

const ICON_BUTTON_TRANSIENT_PROPS = new Set([
  '$color',
  '$variant',
  '$size',
  '$roundness',
  '$selected',
  '$loading',
]);

export const StyledIconButton = styled(Button, {
  shouldForwardProp: (prop) => !ICON_BUTTON_TRANSIENT_PROPS.has(prop),
})<{
  $color: ButtonColor;
  $variant: ButtonVariant;
  $size: IconButtonSize;
  $roundness: ButtonRoundness;
  $selected: boolean;
  $loading: boolean;
}>(({ theme, $color, $variant, $size, $roundness, $selected, $loading }) => {
  const { padding, iconSize, radius } = ICON_BUTTON_SIZE_CONFIG[$size];
  const isGhost = $variant === 'ghost';

  return {
    // A selected ghost button stays transparent and carries the state in the icon colour.
    ...getBaseStyles(theme, $color, $variant, $roundness, radius, $selected && !isGhost, $loading),
    padding,

    ...(isGhost &&
      $selected && {
        color: theme.colors.icon.brand,
        [forHover]: {
          [`&:hover${DISABLED_EXCLUSIONS}`]: {
            backgroundColor: theme.colors.brand.subtleHover,
          },
        },
        [`&:active${DISABLED_EXCLUSIONS}`]: {
          backgroundColor: theme.colors.brand.subtleActive,
        },
      }),

    svg: {
      width: iconSize,
      height: iconSize,
      flexShrink: 0,
    },
  };
});
